"use client";

import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import {
  SortableStageList,
  splitStages,
  type StageItem,
} from "@/features/jobs/components/stage-cards";
import type { PresetFormValues } from "./schema";

interface StagePresetEditorProps {
  form: UseFormReturn<PresetFormValues>;
}

export function StagePresetEditor({ form }: StagePresetEditorProps) {
  const stages = form.watch("stages");

  const handleAddStage = useCallback(
    (name: string, color: string, description: string) => {
      const newStage: StageItem = {
        id: `custom-${Date.now()}`,
        name,
        color,
        description,
        is_system: false,
      };
      const { leading, editable, trailing } = splitStages(stages);
      form.setValue("stages", [...leading, ...editable, newStage, ...trailing], {
        shouldValidate: true,
      });
    },
    [form, stages],
  );

  const handleRemoveStage = useCallback(
    (id: string) => {
      form.setValue(
        "stages",
        stages.filter((stage) => stage.id !== id),
        { shouldValidate: true },
      );
    },
    [form, stages],
  );

  const handleUpdateStage = useCallback(
    (id: string, patch: Partial<StageItem>) => {
      form.setValue(
        "stages",
        stages.map((stage) => (stage.id === id ? { ...stage, ...patch } : stage)),
        { shouldValidate: true },
      );
    },
    [form, stages],
  );

  const handleReorder = useCallback(
    (reordered: StageItem[]) => {
      form.setValue("stages", reordered, { shouldValidate: true });
    },
    [form],
  );

  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border bg-surface-header p-4">
      <SortableStageList
        stages={stages}
        onUpdate={handleUpdateStage}
        onRemove={handleRemoveStage}
        onAdd={handleAddStage}
        onReorder={handleReorder}
      />
    </div>
  );
}
